import { ChangeDetectorRef, Component, HostListener, inject, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';

import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { OurAuthService } from './core/auth/auth.service';
import { ChatWidgetComponent } from './account/tutorials/chat-widget/chat-widget.component';
import { ChatWidgetVisibilityService } from './account/tutorials/chat-widget/chat-widget-visibility.service';
import { RouteLoaderComponent } from './layout/common/route-loader/route-loader.component';
import { NotificationToastComponent } from './layout/common/notification-toast/notification-toast.component';
import { SaiQuickTourComponent } from './ui/sai-quick-tour/sai-quick-tour.component';
import { SaiQuickTourService } from './ui/sai-quick-tour/sai-quick-tour.service';
import { filter, Subject, takeUntil } from 'rxjs';
import { NgIf } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { environment } from 'environments/environment';

/** Public marketing paths from `website/website.routes.ts` — tutorial agent must not appear here. */
const WEBSITE_PATHS: readonly string[] = [
  '/home',
  '/about',
  '/agents',
  '/contact',
  '/faq',
  '/privacy',
  '/terms',
  '/thanks',
];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    RouterOutlet,
    ChatWidgetComponent,
    RouteLoaderComponent,
    NotificationToastComponent,
    SaiQuickTourComponent,
    NgIf,
    TranslocoModule
  ],
})
export class AppComponent implements OnDestroy, OnInit {
  /** SAI Assistant launcher — only for signed-in users on authenticated app pages (not marketing site). */
  showChatWidget: boolean = false;
  /** SAI Quick Tour — gated by `environment.showQuickTour`. */
  showQuickTour: boolean = environment.showQuickTour;
  lang: string = 'en';
  private _isLoggedIn: boolean = false;
  private _routeAllowsChatWidget: boolean = false;
  private _chatWidgetSuppressed: boolean = false;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private _quickTourService = inject(SaiQuickTourService);
  private _chatWidgetVisibility = inject(ChatWidgetVisibilityService);
  /** ngx-color-picker `cpUseRootViewContainer` anchor — keeps popups out of overflow-hidden board columns. */
  constructor(
    private _authService: OurAuthService,
    private _router: Router,
    private _translocoService: TranslocoService,
    private _changeDetectorRef: ChangeDetectorRef,
    public vcRef: ViewContainerRef,
  ) {
    this._routeAllowsChatWidget = this.isChatWidgetAllowedOnUrl(this._router.url);

    this._authService.isLoggedIn$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((isLoggedIn: boolean) => {
        this._isLoggedIn = isLoggedIn;
        this.refreshChatWidgetVisibility();
        if (this.showQuickTour && isLoggedIn) {
          this._quickTourService.scheduleAutoStartIfEligible();
        }
      });

    this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this._unsubscribeAll)
    ).subscribe((event: NavigationEnd) => {
      this._routeAllowsChatWidget = this.isChatWidgetAllowedOnUrl(event.urlAfterRedirects || event.url);
      this.refreshChatWidgetVisibility();
    });

    this._chatWidgetVisibility.suppressed$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((suppressed) => {
        this._chatWidgetSuppressed = suppressed;
        this.refreshChatWidgetVisibility();
      });
  }

  ngOnInit() {
    this._translocoService.langChanges$.pipe(takeUntil(this._unsubscribeAll)).subscribe((res) => {
      this.lang = res;
      document.body.setAttribute('dir', this.lang === 'ar' ? 'rtl' : 'ltr');
      this._changeDetectorRef.markForCheck();
    })

    document.addEventListener('wheel', (event) => {
      const target = document.activeElement as HTMLInputElement;
      if (target && target.type === 'number') {
        target.blur();
      }
    }, { passive: false });
  }

  private refreshChatWidgetVisibility(): void {
    this.showChatWidget =
      this._isLoggedIn && this._routeAllowsChatWidget && !this._chatWidgetSuppressed;
    this._changeDetectorRef.markForCheck();
  }

  /** True when the current URL is an authenticated-app page that should host the tutorial agent. */
  private isChatWidgetAllowedOnUrl(url: string): boolean {
    const path = url.split('?')[0];

    if (WEBSITE_PATHS.some(p => path === p || path.startsWith(`${p}/`))) {
      return false;
    }

    return !(
      // The messenger app is a chat product in its own right; the tutorial launcher does not belong in it.
      path.startsWith('/messenger') ||
      path.includes('chat') ||
      path.includes('sign') ||
      path.includes('otp') ||
      path.includes('password') ||
      path.includes('custom-agents') ||
      path.includes('/report/') ||
      path.includes('report-new') ||
      // The dashboard viewer, for the same reason `/report/` is here: it is a full-bleed document
      // on a `layout: 'empty'` route, so a floating launcher at `z-99999` sits on top of the
      // dashboard's own bottom-right content rather than beside a page's chrome.
      path.includes('/dashboards/view/') ||
      path.includes('/form/') ||
      path.includes('add-tracker')
    );
  }

  // @HostListener('document:contextmenu', ['$event'])
  // onRightClick(event: MouseEvent) {
  //   event.preventDefault();
  // }

  @HostListener('document:keydown', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    // Check if Ctrl + U is pressed
    if (event.ctrlKey && event.key.toLowerCase() === 'u') {
      event.preventDefault(); // Prevent the default behavior (View Source)
    }
  }

  // Listen for the beforeunload event to make the user offline
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    this._authService.makeItOffline();
  }

  ngOnDestroy() {
    this._authService.makeItOffline();

    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

}

if (typeof Worker !== 'undefined') {
  // Create a new
  const worker = new Worker(new URL('./app.worker', import.meta.url));
  worker.onmessage = ({ data }) => {
  };
  worker.postMessage('hello');
} else {
  // Web Workers are not supported in this environment.
  // You should add a fallback so that your program still executes correctly.
}